import { useEffect } from "react";
import type { Movie } from "@/mocks/movies";

type PlayerDemoProps = {
  movie: Movie;
  open: boolean;
  onClose: () => void;
};

export default function PlayerDemo({ movie, open, onClose }: PlayerDemoProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/85 px-4 py-8 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="播放演示"
      onClick={onClose}
    >
      <div
        className="animate-fade-up w-full max-w-3xl overflow-hidden rounded-lg border border-background-50/15 bg-foreground-950"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-background-50/10 px-4 py-3 md:px-5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-background-50">{movie.title}</p>
            <p className="font-label text-[11px] text-background-300">播放演示 · Demo Mode</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭播放演示"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-background-200 transition-colors hover:bg-background-50/10 hover:text-background-50"
          >
            <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
        </div>

        <div className="relative aspect-video w-full overflow-hidden bg-foreground-900">
          <img
            src={movie.poster}
            alt={`${movie.title} 播放演示画面`}
            className="h-full w-full object-cover opacity-25 blur-[2px]"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-50/30 bg-background-50/10">
              <i className="ri-play-fill text-2xl w-6 h-6 flex items-center justify-center text-background-100" />
            </span>
            <p className="mt-4 text-sm font-medium text-background-50">
              播放功能为前端演示，后续接入中
            </p>
            <p className="mt-2 max-w-md text-xs leading-relaxed text-background-300">
              当前页面不加载任何真实视频流，仅展示播放器区域与交互位置。接入片源服务后，此处将替换为正式播放器。
            </p>
          </div>
        </div>

        <div className="px-4 py-4 md:px-5">
          <div className="flex items-center gap-3">
            <span className="font-label text-[11px] text-background-300">00:00</span>
            <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-background-50/20">
              <span className="absolute inset-y-0 left-0 w-0 rounded-full bg-primary-500" />
            </div>
            <span className="font-label text-[11px] text-background-300">
              {String(movie.duration).padStart(2, "0")}:00
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-full border border-background-50/20 text-background-300">
              <i className="ri-volume-mute-line text-sm w-4 h-4 flex items-center justify-center" />
            </span>
          </div>
          <div className="mt-4 flex flex-col gap-2 border-t border-background-50/10 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] text-background-400">
              演示模式：进度条与音量控件不可用，不产生播放记录。
            </p>
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer whitespace-nowrap rounded-md bg-background-50/10 px-3 py-1.5 text-xs text-background-100 transition-colors hover:bg-background-50/20"
            >
              我知道了
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}